import * as React from 'react';

import { FormEvent, InputHTMLAttributes, KeyboardEvent } from 'react';
import { withMedia } from 'react-media-query-hoc';

import { interaction } from '@origin-digital/reporting-client';

import { ButtonAction } from '../Button';
import { Byline, Heading3 } from '../Text';
import { MediaProps, RippleLine } from '../SignUp/SignUp';
import { FindPlanContainer, FindPlanInner, PostCodeLabel, PostCodeInput } from './FindPlan.styles';
import { getComparePlanPath } from '../../utils/utils';

type TargetEvent = FormEvent<InputHTMLAttributes<HTMLInputElement>>;

interface FindPlanPureProps {
  headline: string;
  byline: string;
  buttonText: string;
  buttonUrl: string;
  postCodeValue: string;
  topline: string;
  onChange?: (e: any) => void;
  onKeyPress?: (e: any) => void;
  onSubmit?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  media: MediaProps;
  active: boolean;
}

const tracking = (trackingId, trackingLabel) => {
  interaction('click', {
    componentType: 'button', //Required
    id: trackingId, //Required; second component of data-id
    parentContainer: 'homepage', //Required; first component of data-id
    appName: 'homepage', //Required
    label: trackingLabel, //Required; Text displayed on the button
  });
};

export const FindPlanPure = withMedia(
  ({
    headline,
    byline,
    buttonText,
    buttonUrl,
    postCodeValue,
    topline,
    onChange,
    onKeyPress,
    onSubmit,
    active,
    onFocus,
    onBlur,
  }: FindPlanPureProps) => (
    <FindPlanContainer data-comment="FindPlan">
      <FindPlanInner>
        <Heading3>{headline}</Heading3>
        <Byline>{byline}</Byline>
      </FindPlanInner>
      <FindPlanInner>
        {/*HACK for Andriod to show number keyboard, "number" doesnt work with (. -)*/}
        {/*3000 - default for now until we get location services*/}
        <PostCodeLabel>{topline}</PostCodeLabel>
        <PostCodeInput
          value={postCodeValue}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyPress={onKeyPress}
          inputmode="numeric"
          pattern="[0-9]*"
          title="Postal Code"
          placeholder={3000}
          type="tel"
        />
        <RippleLine active={active} />
        <ButtonAction href={buttonUrl} onClick={onSubmit!}>
          {buttonText}
        </ButtonAction>
      </FindPlanInner>
    </FindPlanContainer>
  ),
);

export class FindPlan extends React.Component<any, { postCodeValue: string; active: boolean }> {
  POSTCODE_LENGTH = 4;

  constructor(props: any, state: any) {
    super(props, state);
    this.state = {
      postCodeValue: '',
      active: false,
    };
  }

  onKeyPress = (event: KeyboardEvent<HTMLFormElement>) => {
    // these are valid "number" ie '-2e10', but not valid for post codes
    if (event.key === 'Enter') {
      this.onSubmit();
    } else if (event.key.match(/[0-9]/) === null) {
      event.preventDefault();
    }
  };

  onFocus = () => {
    this.setState({ active: true });
  };
  onBlur = () => {
    this.setState({ active: false });
  };

  onChange = (event: TargetEvent) => {
    const value = (event.currentTarget.value as string).replace(/[^0-9]/gi, '');

    const postCodeValue =
      value.length > this.POSTCODE_LENGTH // do not let the field pass POSTCODE_LENGTH
        ? this.state.postCodeValue
        : value.length === 0
        ? ''
        : value;

    this.setState({ postCodeValue });

    if (value.length === this.POSTCODE_LENGTH) {
      this.onSubmit(postCodeValue);
    }
  };

  onSubmit = (postCodeValue?: string) => {
    const postcode = typeof postCodeValue === 'string' ? postCodeValue : this.state.postCodeValue;
    tracking(this.props.trackingId, this.props.trackingLabel);
    const path = getComparePlanPath(postcode);
    window.location.href = path;
  };

  render() {
    const ClientComponent = this.props.component;
    return (
      <ClientComponent
        data-comment={this.props.dataComment}
        onKeyPress={this.onKeyPress}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onChange={this.onChange}
        onSubmit={this.onSubmit}
        {...this.state}
        {...this.props}
      />
    );
  }
}
